import React from "react";
import styles from "./SystemMessage.module.css";
import { MessageType } from "../../../Types/Analyse/MessageType";
import { McpDebugTurn } from "../../../Types/Analyse/AnalyseChatData";
import LoadingWrapper from "../../Utils/Loading/LoadingWrapper";
import CopyWrapper from "../../Utils/CopyWrapper/CopyWrapper";
import Rating, { RatingValue } from "../../Utils/Rating/Rating";
import ToolActivityDebug from "../ToolActivityDebug/ToolActivityDebug";

type SystemMessageProps = {
  messageType?: MessageType;
  loading?: boolean;
  children?: React.ReactNode;
  ratingChanged?: (value: RatingValue) => void;
  feedbackChanged?: (message: string) => void;
  rating?: RatingValue;
  feedback?: string;
  /** When false, hides Copy Text and rating (e.g. while the reply is still streaming). */
  showActions?: boolean;
  /** Tool/function activity for this reply (debug). */
  debugTurns?: McpDebugTurn[] | null;
  error?: string;
  cancelled?: boolean;
  onRetry?: () => void;
};

const SystemMessage = ({
  messageType,
  loading,
  children,
  ratingChanged,
  feedbackChanged,
  rating,
  feedback,
  showActions = true,
  debugTurns,
  error,
  cancelled,
  onRetry,
}: SystemMessageProps) => {
  const isUser = messageType === MessageType.User;

  if (isUser) {
    return (
      <div className={styles.wrapper}>
        <div className={`${styles.container} ${styles.user} ${loading ? styles.loading : ""}`}>
          <div className={styles.loadContainer}>
            <LoadingWrapper loading={loading}>
              <div className={styles.body}>{children}</div>
            </LoadingWrapper>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.container} ${styles.system} ${loading ? styles.loading : ""}`}
        data-testid="analyse-assistant-response"
      >
        <div className={styles.loadContainer}>
          <LoadingWrapper loading={loading}>
            <div className={styles.systemCard}>
              <ToolActivityDebug turns={debugTurns} embedded />

              {children != null && children !== false && (
                <div className={styles.responseSection}>
                  {showActions && !error && !cancelled ? (
                    <CopyWrapper className={styles.copyArea}>
                      <div className={styles.body}>{children}</div>
                    </CopyWrapper>
                  ) : (
                    <div className={styles.bodyPad}>
                      <div className={styles.body}>{children}</div>
                    </div>
                  )}
                </div>
              )}

              {cancelled && (
                <div className={styles.statusBar} role="status">
                  <span>Generation stopped. Partial response kept.</span>
                </div>
              )}

              {error && !cancelled && (
                <div className={styles.errorBar} role="alert">
                  <span>{error}</span>
                  {onRetry && (
                    <button type="button" className={styles.retryButton} onClick={onRetry}>
                      Retry
                    </button>
                  )}
                </div>
              )}

              {showActions && !error && !cancelled && (
                <div className={styles.feedbackBar}>
                  <Rating
                    value={rating}
                    feedbackValue={feedback}
                    onChange={ratingChanged}
                    onFeedbackChange={feedbackChanged}
                    embedded
                  />
                </div>
              )}
            </div>
          </LoadingWrapper>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SystemMessage);
